import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@lib/cn'
import { LoadingSpinner } from './LoadingSpinner'

const variants = {
  primary: 'bg-[var(--color-btn-primary)] text-white hover:bg-[var(--color-btn-primary-hover)] shadow-sm hover:shadow',
  secondary:
    'bg-[var(--color-surface)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
  ghost: 'bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
  outline:
    'bg-transparent border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]',
}

const sizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2',
  lg: 'px-6 py-3 text-lg',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants
  size?: keyof typeof sizes
  isLoading?: boolean
  children: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'rounded-lg font-medium transition-all duration-200 inline-flex items-center justify-center gap-2 active:scale-95 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none',
        variants[variant],
        sizes[size],
        (disabled || isLoading) && 'opacity-50 cursor-not-allowed active:scale-100',
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? <LoadingSpinner size="sm" /> : children}
    </button>
  )
}
